import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Loader2, Check, X } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";

type RolInvitado = "mandante" | "contratista";

interface MostrarRelacionResponse {
  invitador_nombre: string;
  invitado_nombre: string;
  rol_invitado: RolInvitado;
}

export function AceptarVinculacion() {
  const [, navigate] = useLocation();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const relacionId = params.get("relacion");
  const tipo = useMemo<RolInvitado>(() => {
    const raw = params.get("tipo");
    return raw === "contratista" ? "contratista" : "mandante";
  }, [params]);
  const signedQuery = useMemo(() => buildSignedQuery(params), [params]);

  const endpointBase = useMemo(
    () =>
      tipo === "contratista"
        ? `/auth/contratistas/relacion/${relacionId}`
        : `/auth/mandantes/relacion/${relacionId}`,
    [tipo, relacionId],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [info, setInfo] = useState<MostrarRelacionResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resultado, setResultado] = useState<"aceptada" | "rechazada" | null>(null);
  const [decidiendo, setDecidiendo] = useState(false);

  useEffect(() => {
    if (!relacionId || !signedQuery) {
      setErrorMsg("El enlace es inválido o está incompleto.");
      setIsLoading(false);
      return;
    }

    api
      .get<MostrarRelacionResponse>(`${endpointBase}${signedQuery}`)
      .then((res) => setInfo(res))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 410) {
          setErrorMsg("Esta invitación ya fue respondida.");
        } else if (err instanceof ApiError && err.status === 403) {
          setErrorMsg("El enlace expiró o no es válido.");
        } else {
          setErrorMsg("No se pudo cargar la invitación.");
        }
      })
      .finally(() => setIsLoading(false));
  }, [relacionId, signedQuery, endpointBase]);

  const decidir = async (decision: "aceptar" | "rechazar") => {
    setDecidiendo(true);
    try {
      await api.post(`${endpointBase}${signedQuery}`, { decision });
      setResultado(decision === "aceptar" ? "aceptada" : "rechazada");
      toast.success(decision === "aceptar" ? "Vinculación aceptada." : "Vinculación rechazada.");
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "No se pudo procesar la respuesta.");
      } else {
        toast.error("No se pudo procesar la respuesta.");
      }
    } finally {
      setDecidiendo(false);
    }
  };

  const rol = info?.rol_invitado ?? tipo;

  const titulo =
    rol === "contratista" ? "Te invitaron como contratista" : "Te invitaron como mandante";

  const aceptadaTexto =
    rol === "contratista"
      ? "Vinculación aceptada. Ya puedes colaborar como contratista en los contratos de esta empresa."
      : "Vinculación aceptada. Ya puedes auditar a esta empresa como mandante.";

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground mx-auto mb-4">
            <FileText className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Facil Control</h1>
          <p className="text-muted-foreground mt-2">Solicitud de vinculación</p>
        </div>

        <Card className="border-0 shadow-lg">
          {isLoading ? (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          ) : errorMsg ? (
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-sm text-muted-foreground">{errorMsg}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>
                Ir al login
              </Button>
            </CardContent>
          ) : resultado ? (
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-sm">
                {resultado === "aceptada"
                  ? aceptadaTexto
                  : "Vinculación rechazada. La empresa que te invitó fue notificada."}
              </p>
              <Button onClick={() => navigate("/login")}>Iniciar sesión</Button>
            </CardContent>
          ) : info ? (
            <>
              <CardHeader>
                <CardTitle className="text-2xl">{titulo}</CardTitle>
                <CardDescription>
                  {info.rol_invitado === "contratista" ? (
                    <>
                      <strong>{info.invitador_nombre}</strong> te invita a colaborar como
                      contratista desde tu empresa <strong>{info.invitado_nombre}</strong>. ¿Quieres
                      aceptar?
                    </>
                  ) : (
                    <>
                      <strong>{info.invitador_nombre}</strong> te invita a auditar sus contratos y
                      documentos como mandante desde tu empresa{" "}
                      <strong>{info.invitado_nombre}</strong>. ¿Quieres aceptar?
                    </>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => decidir("aceptar")}
                  disabled={decidiendo}
                  className="flex-1 gap-2"
                >
                  {decidiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Aceptar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => decidir("rechazar")}
                  disabled={decidiendo}
                  className="flex-1 gap-2"
                >
                  <X className="h-4 w-4" />
                  Rechazar
                </Button>
              </CardContent>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

function buildSignedQuery(params: URLSearchParams): string {
  const expires = params.get("expires");
  const signature = params.get("signature");
  if (!expires || !signature) return "";
  const sp = new URLSearchParams();
  sp.set("expires", expires);
  sp.set("signature", signature);
  return `?${sp.toString()}`;
}
